import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { catchError, first, of } from 'rxjs';
import { SpacesService } from '../services/spaces.service';
import { CollaborativeApps } from '../sessions/session/iframe/additional-apps.utils';
import { FlagsService } from '../services/flags.service';
import { URL_REGEX } from '../common/utils/form-validators';

export enum UrlValidationError {
  NoError,
  MalformedUrl, // Url does not pass the local validation Regex
  InvalidUrlResponse, // Url response returned an error code
  NotEmbeddable, // Url embedding is explicitly disallowed
  CustomError, // Custom error message
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class IFrameUtils {
  constructor(
    public dialog: MatDialog,
    private spacesService: SpacesService,
    private featureFlagService: FlagsService,
  ) {}

  async validateUrl(
    url: string,
    validationString: string | null = null,
  ): Promise<{ error: UrlValidationError; modifiedUrl: string }> {
    let error = UrlValidationError.NoError;

    const handleResponse = (validationError: UrlValidationError, modifiedUrl: string = url) => ({
      error: validationError,
      modifiedUrl,
    });
    const isMarioDeveloper = this.featureFlagService.featureFlagsVariables.mario
      .enable_mario_developer_mode
      ? true
      : false;

    if (isMarioDeveloper) {
      return handleResponse(UrlValidationError.NoError, url); // Pass through all URLs without validation
    }

    if (!this.isValidUrl(url)) {
      return handleResponse(UrlValidationError.MalformedUrl);
    } else if (validationString && !url.includes(validationString)) {
      return handleResponse(UrlValidationError.CustomError);
    } else {
      const modifiedUrl = this.addHttpsIfItDoesNotExist(url);

      return new Promise((resolve, reject) => {
        this.spacesService
          .validateIframeUrl(modifiedUrl)
          .pipe(catchError(() => of(false)))
          .pipe(first())
          .subscribe((res: any) => {
            if (!res) {
              return UrlValidationError.InvalidUrlResponse;
            } else if (!res.valid) {
              error =
                res.reason === 'INVALID_SITE'
                  ? UrlValidationError.InvalidUrlResponse
                  : UrlValidationError.NotEmbeddable;
              resolve(handleResponse(error));
            } else {
              resolve(handleResponse(UrlValidationError.NoError, modifiedUrl));
            }
          });
      });
    }
  }

  isValidUrl(url: string): boolean {
    const urlPattern = new RegExp(URL_REGEX, 'i');
    return !!urlPattern.test(url);
  }

  addHttpsIfItDoesNotExist(url: string): string {
    if (!/^(?:f|ht)tps?:\/\//.test(url)) {
      url = `https://${url}`;
    } else if (/^(?:f|ht)tp?:\/\//.test(url)) {
      url = url.replace('http', 'https');
    }
    return url;
  }

  isValidPinCode(pinCode: string): boolean {
    const pinCodePattern = /^[0-9a-zA-Z]+$/;
    return !!pinCodePattern.test(pinCode);
  }

  appTypeFromPastedLink(textClipboard: string): CollaborativeApps | undefined {
    const docsLinkRegex =
      /^(https?:\/\/)?docs\.google\.com\/document\/d\/[a-zA-Z0-9_-]+(\/(edit|preview)?)?(\?.*)?(#.*)?$/g;
    const sheetsLinkRegex =
      /^(https?:\/\/)?docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9_-]+(\/(edit|preview)?)?(\?.*)?(#.*)?$/g;
    const slidesLinkRegex =
      /^(https?:\/\/)?docs\.google\.com\/presentation\/d\/[a-zA-Z0-9_-]+(\/(edit|preview)?)?(\?.*)?(#.*)?$/g;
    if (docsLinkRegex.test(textClipboard)) {
      return CollaborativeApps.GOOGLE_DOCS;
    }
    if (slidesLinkRegex.test(textClipboard)) {
      return CollaborativeApps.GOOGLE_SLIDES;
    }
    if (sheetsLinkRegex.test(textClipboard)) {
      return CollaborativeApps.GOOGLE_SHEETS;
    }
    if (this.isYoutubeLink(textClipboard)) {
      return CollaborativeApps.YOUTUBE;
    }
  }

  private isYoutubeLink(textClipboard: string): boolean {
    try {
      const { hostname, pathname } = new URL(this.addHttpsIfItDoesNotExist(textClipboard.trim()));
      if (pathname.startsWith('/shorts/')) {
        // excluding shorts because it is not supported in the mario Youtube app
        return false;
      }
      return hostname === 'youtu.be' || /(^|\.)youtube\.com$/.test(hostname);
    } catch {
      return false;
    }
  }
}
